import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import Receipt from './Receipt';
import { isStoreParcelOrder } from '../utils/orderType';

const PackagedOrders = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchPhone, setSearchPhone] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [selectedOrderForReceipt, setSelectedOrderForReceipt] = useState(null);

  useEffect(() => {
    fetchPackagedOrders();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [orders, searchPhone, startDate, endDate]);

  const fetchPackagedOrders = async () => {
    try {
      setLoading(true);
      const ordersRef = collection(db, 'orders');
      
      const q = query(
        ordersRef,
        where('packagingComplete', '==', true),
        where('ready_for_pickup', '==', true),
        where('status', '==', 'Handed over to Courier')
      );
      
      const querySnapshot = await getDocs(q);
      const ordersData = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();

        // Store-to-store parcels have their own dedicated "Store Parcels"
        // tab — keep them out of this stock-order list.
        if (isStoreParcelOrder(data)) {
          return;
        }

        ordersData.push({
          id: doc.id,
          ...data
        });
      });
      
      // Sort by created_at descending
      ordersData.sort((a, b) => {
        const dateA = a.created_at?.toDate ? a.created_at.toDate() : new Date(a.created_at || 0);
        const dateB = b.created_at?.toDate ? b.created_at.toDate() : new Date(b.created_at || 0);
        return dateB - dateA;
      });
      
      setOrders(ordersData);
    } catch (error) {
      console.error('Error fetching packaged orders:', error);
      alert('Failed to fetch packaged orders: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...orders];

    // Filter by phone number
    if (searchPhone.trim()) {
      filtered = filtered.filter(order => 
        order.customerInfo?.phone?.includes(searchPhone.trim())
      );
    }

    // Filter by date range
    if (startDate && endDate) {
      // If both dates selected, use the range
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      
      filtered = filtered.filter(order => {
        const packagingDate = order.packagingCompletedAt?.toDate ? order.packagingCompletedAt.toDate() : new Date(order.packagingCompletedAt);
        return packagingDate >= start && packagingDate <= end;
      });
    } else if (startDate) {
      // If only start date selected
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      filtered = filtered.filter(order => {
        const packagingDate = order.packagingCompletedAt?.toDate ? order.packagingCompletedAt.toDate() : new Date(order.packagingCompletedAt);
        return packagingDate >= start;
      });
    } else if (endDate) {
      // If only end date selected
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(order => {
        const packagingDate = order.packagingCompletedAt?.toDate ? order.packagingCompletedAt.toDate() : new Date(order.packagingCompletedAt);
        return packagingDate <= end;
      });
    } else {
      // Default: Show only today's packaged orders
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);
      
      filtered = filtered.filter(order => {
        const packagingDate = order.packagingCompletedAt?.toDate ? order.packagingCompletedAt.toDate() : new Date(order.packagingCompletedAt);
        return packagingDate >= today && packagingDate <= todayEnd;
      });
    }

    setFilteredOrders(filtered);
  };

  const clearFilters = () => {
    setSearchPhone('');
    setStartDate('');
    setEndDate('');
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleOrderDetails = (orderId) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  const handlePrintReceipt = (order) => {
    setSelectedOrderForReceipt(order);
  };

  const handleCloseReceipt = () => {
    setSelectedOrderForReceipt(null);
  };

  if (selectedOrderForReceipt) {
    return <Receipt order={selectedOrderForReceipt} onClose={handleCloseReceipt} />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-2xl font-semibold text-gray-700">Loading packaged orders...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-white shadow-md">
        <div className="px-8 py-6">
          <h2 className="text-2xl font-bold text-gray-800">Packaged Orders</h2>
          <p className="text-sm text-gray-600 mt-1">View all completed packaged orders</p>
        </div>
      </div>

      <div className="px-8 py-6">
        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Search & Filter</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="text"
                value={searchPhone}
                onChange={(e) => setSearchPhone(e.target.value)}
                placeholder="Search by phone..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex items-end gap-2">
              <button
                onClick={clearFilters}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
              >
                Clear Filters
              </button>
              <button
                onClick={fetchPackagedOrders}
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
              >
                Refresh
              </button>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredOrders.length} of {orders.length} total orders
          </div>
        </div>

        {/* Orders Table */}
        {filteredOrders.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-600 text-lg">
              {orders.length === 0 
                ? 'No packaged orders found' 
                : 'No orders match your search criteria'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Products
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrders.map((order) => (
                  <React.Fragment key={order.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {order.customerInfo?.name || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {order.customerInfo?.phone || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 font-mono">
                          {order.orderId || order.id}
                        </div>
                        <div className="text-xs text-gray-500">
                          {order.steadfastConsignmentId || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {order.cartItems?.length || 0} items
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">
                          {formatDate(order.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Packaged
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => toggleOrderDetails(order.id)}
                            className="text-blue-600 hover:text-blue-900 font-medium px-3 py-1 rounded border border-blue-600 hover:bg-blue-50"
                          >
                            {expandedOrderId === order.id ? 'Hide' : 'View'}
                          </button>
                          <button
                            onClick={() => handlePrintReceipt(order)}
                            className="bg-green-500 hover:bg-green-600 text-white font-medium px-3 py-1 rounded flex items-center gap-1"
                          >
                            🖨️ Print
                          </button>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Expanded Details Row */}
                    {expandedOrderId === order.id && (
                      <tr>
                        <td colSpan="6" className="px-6 py-4 bg-gray-50">
                          <div className="space-y-4">
                            <h4 className="font-semibold text-gray-800 text-lg">Order Details</h4>
                            
                            {/* Products */}
                            <div>
                              <h5 className="font-medium text-gray-700 mb-2">Products:</h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {order.cartItems?.map((item, index) => (
                                  <div key={index} className="flex items-center gap-3 bg-white p-3 rounded-lg border">
                                    <img
                                      src={item.image}
                                      alt={item.name}
                                      className="w-16 h-16 object-cover rounded"
                                      onError={(e) => {
                                        e.target.src = 'https://via.placeholder.com/64';
                                      }}
                                    />
                                    <div className="flex-1">
                                      <div className="text-sm font-medium text-gray-900">
                                        {item.name}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        Qty: {item.quantity} × ৳{item.price}
                                      </div>
                                      {item.barcode && (
                                        <div className="text-xs text-gray-400 font-mono">
                                          Barcode: {item.barcode}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Customer Info */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="bg-white p-4 rounded-lg border">
                                <h5 className="font-medium text-gray-700 mb-2">Customer Information:</h5>
                                <div className="space-y-1 text-sm">
                                  <p><span className="font-medium">Name:</span> {order.customerInfo?.name || 'N/A'}</p>
                                  <p><span className="font-medium">Phone:</span> {order.customerInfo?.phone || 'N/A'}</p>
                                  <p><span className="font-medium">Address:</span> {order.customerInfo?.address || 'N/A'}</p>
                                </div>
                              </div>

                              <div className="bg-white p-4 rounded-lg border">
                                <h5 className="font-medium text-gray-700 mb-2">Order Information:</h5>
                                <div className="space-y-1 text-sm">
                                  <p><span className="font-medium">Total Amount:</span> ৳{order.totalPrice || 'N/A'}</p>
                                  <p><span className="font-medium">Payment Method:</span> {order.paymentMethod || 'N/A'}</p>
                                  <p><span className="font-medium">Delivery Area:</span> {order.deliveryArea || 'N/A'}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PackagedOrders;